import { useEffect, useRef, useState } from 'react';

/**
 * Oral history bulk upload — one transcript at a time.
 *
 * Flow:
 *   1. select  — admin picks a file, format (Auto is default), and any
 *                batch-level overrides. Clicking "Preview" uploads the file
 *                to /oral-histories/preview; the backend extracts, parses,
 *                chunks, fingerprints, and attempts to match the
 *                interviewee against the people table.
 *   2. preview — admin confirms the person match (or creates a new person /
 *                leaves unlinked), tweaks interviewer/date, and submits.
 *   3. done    — shows insertion + embedding counts. Admin can start over.
 *
 * The onSubmit prop is called with one of three action payloads:
 *   { type:'oral_history', action:'preview',       ... }
 *   { type:'oral_history', action:'submit',        ... }
 *   { type:'oral_history', action:'peopleSearch',  q }
 *
 * The parent (AdminPortal) translates each to the corresponding HTTP call.
 */

/**
 * Escape a filename for safe inclusion in a SQL string literal. The
 * duplicate-warning banners embed copy-pasteable SQL for cleanup, and a
 * filename containing a single quote (e.g. "70's retrospective.docx")
 * would break naive concatenation. Postgres doubles single quotes to
 * escape them.
 */
function sqlEscape(value) {
  return `'${String(value || '').replace(/'/g, "''")}'`;
}

const OH_FORMATS = [
  {
    id: 'auto',
    label: 'Auto-detect',
    hint: 'Recommended. Format is determined from the file\u2019s name and contents.',
  },
  {
    id: 'otter',
    label: 'Otter.ai export',
    hint: 'Otter transcript export, with or without the summary / keywords header block.',
  },
  {
    id: 'concert_qa',
    label: 'Pre-concert Q&A',
    hint: 'Tony Mowod style: "MM:SS Speaker:" lines.',
  },
  {
    id: 'sewald_qa',
    label: '"We Knew What We Had" documentary',
    hint: 'Jeff Sewald documentary interviews with stage directions and tape cues.',
  },
];

const ACCEPT_EXTS = '.doc,.docx,.pdf,.txt';
const MAX_BYTES = 25 * 1024 * 1024;

const FORMAT_LABEL = {
  auto: 'Auto-detected',
  otter_summary_v2: 'Otter.ai (summary export)',
  otter_named_inline: 'Otter.ai (inline speakers)',
  concert_qa: 'Pre-concert Q&A',
  sewald_qa: 'WKWWH documentary',
};

const INTERVIEW_TYPE_LABEL = {
  oral_history: 'Oral history interview',
  pre_concert_qa: 'Pre-concert Q&A',
  documentary_interview: 'Documentary interview',
};

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function OralHistoryBulkUpload({ onSubmit }) {
  // ── Step 1 state ──
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState('auto');
  const [overridePerson, setOverridePerson] = useState('');
  const [overrideInterviewer, setOverrideInterviewer] = useState('');
  const [overrideDate, setOverrideDate] = useState('');
  const fileRef = useRef(null);

  // ── Process state ──
  const [step, setStep] = useState('select'); // select | preview | done
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // ── Step 2 (preview) state ──
  const [preview, setPreview] = useState(null);
  const [editPerson, setEditPerson] = useState('');
  const [editInterviewer, setEditInterviewer] = useState('');
  const [editDate, setEditDate] = useState('');
  const [personAction, setPersonAction] = useState('link'); // link | create | leave | search
  const [pickedPersonId, setPickedPersonId] = useState(null);
  const [pickedPersonName, setPickedPersonName] = useState('');
  const [newPersonName, setNewPersonName] = useState('');
  const [newPersonBio, setNewPersonBio] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [expandChunks, setExpandChunks] = useState(false);

  // ── Step 3 (result) state ──
  const [result, setResult] = useState(null);

  const activeFormat = OH_FORMATS.find((f) => f.id === format);

  const resetAll = () => {
    setFile(null);
    setFormat('auto');
    setOverridePerson('');
    setOverrideInterviewer('');
    setOverrideDate('');
    setStep('select');
    setBusy(false);
    setError(null);
    setPreview(null);
    setEditPerson('');
    setEditInterviewer('');
    setEditDate('');
    setPersonAction('link');
    setPickedPersonId(null);
    setPickedPersonName('');
    setNewPersonName('');
    setNewPersonBio('');
    setSearchQ('');
    setSearchResults([]);
    setExpandChunks(false);
    setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileSelect = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (picked.size > MAX_BYTES) {
      setError(`File too large (${formatBytes(picked.size)}). Maximum is 25 MB.`);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setError(null);
    setFile(picked);
  };

  // ── Step 1 → Step 2 ──
  const handlePreview = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const data = await onSubmit({
        type: 'oral_history',
        action: 'preview',
        file,
        format,
        overridePerson,
        overrideInterviewer,
        overrideDate,
      });
      setPreview(data);
      setEditPerson(data.person || '');
      setEditInterviewer(data.interviewer || '');
      setEditDate(data.date || '');
      setPickedPersonId(null);
      setPickedPersonName('');
      if (data.personMatch) {
        setPersonAction('link');
      } else {
        setPersonAction('create');
        setNewPersonName(data.person || '');
      }
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Preview failed.');
    } finally {
      setBusy(false);
    }
  };

  // ── People search (debounced) ──
  useEffect(() => {
    if (personAction !== 'search') return;
    const q = searchQ.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setSearchBusy(true);
      try {
        const res = await onSubmit({
          type: 'oral_history',
          action: 'peopleSearch',
          q,
        });
        setSearchResults(res?.results || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchBusy(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchQ, personAction, onSubmit]);

  // ── Step 2 → Step 3 ──
  const submit = async () => {
    if (!file || !preview) return;
    const decision = buildPersonDecision({
      personAction,
      preview,
      pickedPersonId,
      newPersonName,
      newPersonBio,
    });
    if (!decision) {
      setError('Please resolve the interviewee (pick a match, create new, or leave unlinked).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const summary = await onSubmit({
        type: 'oral_history',
        action: 'submit',
        file,
        format,
        overridePerson: editPerson,
        overrideInterviewer: editInterviewer,
        overrideDate: editDate,
        personDecision: decision,
      });
      setResult(summary);
      setStep('done');
    } catch (err) {
      setError(err.message || 'Submission failed.');
    } finally {
      setBusy(false);
    }
  };

  // ── Render ──
  return (
    <div className="BulkUpload-typePane">
      {step === 'select' && (
        <SelectStep
          file={file}
          fileRef={fileRef}
          format={format}
          activeFormat={activeFormat}
          overridePerson={overridePerson}
          overrideInterviewer={overrideInterviewer}
          overrideDate={overrideDate}
          setFormat={setFormat}
          setOverridePerson={setOverridePerson}
          setOverrideInterviewer={setOverrideInterviewer}
          setOverrideDate={setOverrideDate}
          handleFileSelect={handleFileSelect}
          clearFile={() => {
            setFile(null);
            if (fileRef.current) fileRef.current.value = '';
          }}
          onPreview={handlePreview}
          busy={busy}
          error={error}
        />
      )}

      {step === 'preview' && preview && (
        <PreviewStep
          file={file}
          preview={preview}
          editPerson={editPerson}
          setEditPerson={setEditPerson}
          editInterviewer={editInterviewer}
          setEditInterviewer={setEditInterviewer}
          editDate={editDate}
          setEditDate={setEditDate}
          personAction={personAction}
          setPersonAction={setPersonAction}
          pickedPersonId={pickedPersonId}
          setPickedPersonId={setPickedPersonId}
          pickedPersonName={pickedPersonName}
          setPickedPersonName={setPickedPersonName}
          newPersonName={newPersonName}
          setNewPersonName={setNewPersonName}
          newPersonBio={newPersonBio}
          setNewPersonBio={setNewPersonBio}
          searchQ={searchQ}
          setSearchQ={setSearchQ}
          searchResults={searchResults}
          searchBusy={searchBusy}
          expandChunks={expandChunks}
          setExpandChunks={setExpandChunks}
          onSubmit={submit}
          onStartOver={resetAll}
          busy={busy}
          error={error}
        />
      )}

      {step === 'done' && result && (
        <DoneStep summary={result} onStartOver={resetAll} />
      )}
    </div>
  );
}

// ── Step 1: file pick + format + initial overrides ──

function SelectStep(props) {
  const {
    file,
    fileRef,
    format,
    activeFormat,
    overridePerson,
    overrideInterviewer,
    overrideDate,
    setFormat,
    setOverridePerson,
    setOverrideInterviewer,
    setOverrideDate,
    handleFileSelect,
    clearFile,
    onPreview,
    busy,
    error,
  } = props;

  return (
    <>
      <p className="AdminPortal-sectionLead">
        Upload one interview transcript ({ACCEPT_EXTS}). The file is parsed,
        chunked, and checked for duplicates. You&rsquo;ll review and confirm
        the match against the people table before anything is written.
      </p>

      <label className="AdminPortal-field BulkUpload-ohFormat">
        <span className="AdminPortal-fieldLabel">Transcript format</span>
        <select
          className="AdminPortal-select"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        >
          {OH_FORMATS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        <span className="BulkUpload-formatHint">{activeFormat.hint}</span>
      </label>

      <details className="BulkUpload-advanced">
        <summary className="BulkUpload-advancedSummary">
          Pre-fill interviewee / interviewer / date (optional)
        </summary>
        <p className="BulkUpload-overrideHint">
          Leave blank to auto-derive these from the filename. You&rsquo;ll
          be able to edit them in the preview either way.
        </p>
        <div className="BulkUpload-overrideGrid">
          <label className="AdminPortal-field">
            <span className="AdminPortal-fieldLabel">Interviewee name</span>
            <input
              type="text"
              className="AdminPortal-input"
              value={overridePerson}
              onChange={(e) => setOverridePerson(e.target.value)}
              placeholder="e.g. Roger Humphries"
            />
          </label>
          <label className="AdminPortal-field">
            <span className="AdminPortal-fieldLabel">Interviewer</span>
            <input
              type="text"
              className="AdminPortal-input"
              value={overrideInterviewer}
              onChange={(e) => setOverrideInterviewer(e.target.value)}
              placeholder="e.g. Jeff Sewald"
            />
          </label>
          <label className="AdminPortal-field">
            <span className="AdminPortal-fieldLabel">Interview date</span>
            <input
              type="date"
              className="AdminPortal-input"
              value={overrideDate}
              onChange={(e) => setOverrideDate(e.target.value)}
            />
          </label>
        </div>
      </details>

      {error && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {error}
        </div>
      )}

      <div className="BulkUpload-dropzone">
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT_EXTS}
          onChange={handleFileSelect}
          className="BulkUpload-fileInput"
          id="bulk-upload-input-oh"
        />
        <label htmlFor="bulk-upload-input-oh" className="BulkUpload-dropLabel">
          <span className="BulkUpload-dropIcon">+</span>
          <span>Click to select a transcript file</span>
          <span className="BulkUpload-dropHint">
            Accepts {ACCEPT_EXTS} · one file per upload
          </span>
        </label>
      </div>

      {file && (
        <div className="BulkUpload-fileList">
          <div className="BulkUpload-fileListHeader">
            <strong>Selected:</strong>
            <button type="button" className="BulkUpload-clearBtn" onClick={clearFile}>
              Clear
            </button>
          </div>
          <ul className="BulkUpload-fileListItems">
            <li className="BulkUpload-fileListItem">
              <span className="BulkUpload-fileListName">{file.name}</span>
              <span className="BulkUpload-fileListMeta">{formatBytes(file.size)}</span>
            </li>
          </ul>
        </div>
      )}

      <div className="AdminPortal-actions">
        <button
          type="button"
          className="ChatButton AdminPortal-submitBtn"
          disabled={!file || busy}
          onClick={onPreview}
        >
          {busy ? 'Parsing…' : 'Preview transcript'}
        </button>
      </div>
    </>
  );
}

// ── Step 2: preview + editable metadata + person resolution ──

function PreviewStep(props) {
  const {
    file,
    preview,
    editPerson,
    setEditPerson,
    editInterviewer,
    setEditInterviewer,
    editDate,
    setEditDate,
    personAction,
    setPersonAction,
    pickedPersonId,
    setPickedPersonId,
    pickedPersonName,
    setPickedPersonName,
    newPersonName,
    setNewPersonName,
    newPersonBio,
    setNewPersonBio,
    searchQ,
    setSearchQ,
    searchResults,
    searchBusy,
    expandChunks,
    setExpandChunks,
    onSubmit,
    onStartOver,
    busy,
    error,
  } = props;

  const isFingerprintDup = Boolean(preview.fingerprintMatch);
  const sourceFileRowCount = preview.sourceFileRowCount || 0;
  const isFilenameDup = !isFingerprintDup && sourceFileRowCount > 0;

  return (
    <section className="AdminPortal-section BulkUpload-ohPreview">
      <div className="AdminPortal-sectionHeader">
        <h3 className="AdminPortal-sectionTitle">Transcript preview</h3>
        <button
          type="button"
          className="BulkUpload-clearBtn"
          onClick={onStartOver}
        >
          Start over
        </button>
      </div>

      <div className="BulkUpload-ohSummary">
        <div className="BulkUpload-ohSummaryRow">
          <strong className="BulkUpload-ohSummaryLabel">File</strong>
          <span>{preview.fileName}</span>
        </div>
        <div className="BulkUpload-ohSummaryRow">
          <strong className="BulkUpload-ohSummaryLabel">Size</strong>
          <span>
            {file ? `${formatBytes(file.size)} · ` : ''}
            {preview.rawWordCount.toLocaleString()} words ·{' '}
            {preview.rawCharCount.toLocaleString()} chars
          </span>
        </div>
        <div className="BulkUpload-ohSummaryRow">
          <strong className="BulkUpload-ohSummaryLabel">Format</strong>
          <span>
            {FORMAT_LABEL[preview.detectedFormat] || preview.detectedFormat} ·{' '}
            {INTERVIEW_TYPE_LABEL[preview.interviewType] || preview.interviewType}
          </span>
        </div>
        <div className="BulkUpload-ohSummaryRow">
          <strong className="BulkUpload-ohSummaryLabel">Chunks</strong>
          <span>
            {preview.totalChunks} total ·{' '}
            <span className="BulkUpload-countNew">
              {preview.newChunks} new
            </span>
            {preview.duplicateChunks > 0 && (
              <>
                {' '}·{' '}
                <span className="BulkUpload-countDup">
                  {preview.duplicateChunks} already ingested
                </span>
              </>
            )}
          </span>
        </div>
      </div>

      {preview.warnings?.length > 0 && (
        <div className="AdminPortal-banner AdminPortal-banner--warning">
          <strong>Warnings</strong>
          <ul className="BulkUpload-warningList">
            {preview.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {isFingerprintDup && (
        <div className="AdminPortal-banner AdminPortal-banner--warning">
          <strong>Possible duplicate.</strong> This transcript&rsquo;s
          fingerprint matches an existing source in the archive
          (<code>{preview.fingerprintMatch}</code>).{' '}
          {preview.duplicateChunks > 0 && preview.newChunks === 0 && (
            <>All {preview.duplicateChunks} chunks are already ingested —
            submitting will be a no-op.</>
          )}
          {preview.duplicateChunks > 0 && preview.newChunks > 0 && (
            <>{preview.duplicateChunks} of {preview.totalChunks} chunks are
            already in the archive — submitting will insert the remaining{' '}
            {preview.newChunks} as new rows.</>
          )}
          {preview.duplicateChunks === 0 && preview.newChunks > 0 && (
            <>None of the chunks here match existing content hashes, but the
            fingerprint match usually means this interview is already in the
            archive (likely extracted or chunked differently in a previous
            ingest). Submitting will create a second copy — use
            &ldquo;Start over&rdquo; if that isn&rsquo;t what you want.</>
          )}
        </div>
      )}

      {isFilenameDup && (
        <div className="AdminPortal-banner AdminPortal-banner--warning">
          <strong>Possible duplicate (filename match).</strong> The archive
          already has {sourceFileRowCount} row{sourceFileRowCount === 1 ? '' : 's'}{' '}
          stored under <code>{preview.fileName}</code>, but the content
          fingerprint doesn&rsquo;t match — typical when the existing
          rows were ingested by a different text extractor.{' '}
          {preview.duplicateChunks > 0 && preview.newChunks === 0 && (
            <>All {preview.duplicateChunks} chunks here already exist by
            content hash — submitting will be a no-op.</>
          )}
          {preview.duplicateChunks > 0 && preview.newChunks > 0 && (
            <>{preview.duplicateChunks} of {preview.totalChunks} chunks
            match existing content; the other {preview.newChunks} would be
            inserted. If you want a clean re-ingest, delete the existing
            rows first (<code>DELETE FROM oral_histories WHERE
            source_file = {sqlEscape(preview.fileName)}</code>) and re-upload.</>
          )}
          {preview.duplicateChunks === 0 && preview.newChunks > 0 && (
            <>None of the new chunks collide on content hash, and{' '}
            <code>(source_file, chunk_index)</code> will block only the
            first {sourceFileRowCount} indices on submit — anything beyond
            that would land alongside the old rows. Recommended: delete
            the existing rows first (<code>DELETE FROM oral_histories
            WHERE source_file = {sqlEscape(preview.fileName)}</code>) and
            re-upload for a clean record.</>
          )}
        </div>
      )}

      <div className="BulkUpload-ohEditGrid">
        <label className="AdminPortal-field">
          <span className="AdminPortal-fieldLabel">Interviewee name</span>
          <input
            type="text"
            className="AdminPortal-input"
            value={editPerson}
            onChange={(e) => setEditPerson(e.target.value)}
          />
          <span className="BulkUpload-fieldHint">
            Shown in search results and stored as <code>person_name</code>.
          </span>
        </label>
        <label className="AdminPortal-field">
          <span className="AdminPortal-fieldLabel">Interviewer</span>
          <input
            type="text"
            className="AdminPortal-input"
            value={editInterviewer}
            onChange={(e) => setEditInterviewer(e.target.value)}
            placeholder="Leave blank if unknown"
          />
        </label>
        <label className="AdminPortal-field">
          <span className="AdminPortal-fieldLabel">Interview date</span>
          <input
            type="date"
            className="AdminPortal-input"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
          />
        </label>
      </div>

      <PersonResolution
        preview={preview}
        editPerson={editPerson}
        personAction={personAction}
        setPersonAction={setPersonAction}
        pickedPersonId={pickedPersonId}
        setPickedPersonId={setPickedPersonId}
        pickedPersonName={pickedPersonName}
        setPickedPersonName={setPickedPersonName}
        newPersonName={newPersonName}
        setNewPersonName={setNewPersonName}
        newPersonBio={newPersonBio}
        setNewPersonBio={setNewPersonBio}
        searchQ={searchQ}
        setSearchQ={setSearchQ}
        searchResults={searchResults}
        searchBusy={searchBusy}
      />

      <details
        className="BulkUpload-advanced"
        open={expandChunks}
        onToggle={(e) => setExpandChunks(e.target.open)}
      >
        <summary className="BulkUpload-advancedSummary">
          Preview first few chunks
        </summary>
        <ul className="BulkUpload-ohChunkList">
          {preview.chunks.slice(0, 5).map((c) => (
            <li
              key={c.index}
              className={
                'BulkUpload-ohChunk' +
                (c.alreadyIngested ? ' BulkUpload-ohChunk--dup' : '')
              }
            >
              <div className="BulkUpload-ohChunkHeader">
                <span>
                  Chunk {c.index + 1} · {c.chunkType} · {c.wordCount} words
                </span>
                {c.alreadyIngested && (
                  <span className="BulkUpload-countDup">
                    already ingested
                  </span>
                )}
              </div>
              <p className="BulkUpload-ohChunkBody">{c.preview}</p>
            </li>
          ))}
          {preview.chunks.length > 5 && (
            <li className="BulkUpload-ohChunk BulkUpload-ohChunkMore">
              …and {preview.chunks.length - 5} more.
            </li>
          )}
        </ul>
      </details>

      {error && (
        <div className="AdminPortal-banner AdminPortal-banner--error" role="alert">
          {error}
        </div>
      )}

      <div className="AdminPortal-actions">
        <button
          type="button"
          className="ChatButton ChatButton--primary AdminPortal-submitBtn"
          disabled={busy}
          onClick={onSubmit}
        >
          {busy ? 'Submitting…' : 'Confirm & submit'}
        </button>
      </div>
    </section>
  );
}

function PersonResolution(props) {
  const {
    preview,
    editPerson,
    personAction,
    setPersonAction,
    pickedPersonId,
    setPickedPersonId,
    pickedPersonName,
    setPickedPersonName,
    newPersonName,
    setNewPersonName,
    newPersonBio,
    setNewPersonBio,
    searchQ,
    setSearchQ,
    searchResults,
    searchBusy,
  } = props;

  const hasMatch = Boolean(preview.personMatch);

  // Selecting anyone from the search results or the "Other candidates" list
  // always routes into the 'search' (pick-different) slot, so the
  // auto-matched person in "Link to auto-match" stays untouched and the
  // selection remains visible inside its own radio option.
  const pickFromSearch = (r) => {
    setPickedPersonId(r.id);
    setPickedPersonName(r.name);
    setPersonAction('search');
  };

  const clearSearchPick = () => {
    setPickedPersonId(null);
    setPickedPersonName('');
  };

  return (
    <section className="BulkUpload-ohPerson">
      <h4 className="BulkUpload-ohSubHeading">Link interviewee to people table</h4>

      {hasMatch ? (
        <div className="BulkUpload-ohMatch BulkUpload-ohMatch--found">
          <strong>Auto-matched:</strong> {preview.personMatch.name}{' '}
          <span className="BulkUpload-ohMatchMeta">
            #{preview.personMatch.id} · matched via {preview.personMatch.matchedVia}
            {preview.personMatch.matchedOn &&
              preview.personMatch.matchedOn !== preview.person && (
                <> (&ldquo;{preview.personMatch.matchedOn}&rdquo;)</>
              )}
          </span>
          {preview.personCandidates.length > 1 && (
            <div className="BulkUpload-ohAltCandidates">
              <span>Other candidates (click to use instead):</span>
              <ul>
                {preview.personCandidates.slice(1).map((c) => {
                  const isSelected =
                    personAction === 'search' && c.id === pickedPersonId;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={
                          'BulkUpload-ohLinkBtn' +
                          (isSelected ? ' BulkUpload-ohLinkBtn--selected' : '')
                        }
                        onClick={() => pickFromSearch(c)}
                      >
                        {isSelected ? '\u2713 ' : ''}
                        {c.name} #{c.id} ({c.matchedVia})
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="BulkUpload-ohMatch BulkUpload-ohMatch--missing">
          No match for &ldquo;{editPerson || preview.person}&rdquo; in the
          people table.
        </div>
      )}

      <fieldset className="BulkUpload-ohActions">
        <legend className="BulkUpload-ohActionsLegend">What should happen?</legend>

        {hasMatch && (
          <label className="BulkUpload-ohActionOption">
            <input
              type="radio"
              name="personAction"
              value="link"
              checked={personAction === 'link'}
              onChange={() => setPersonAction('link')}
            />
            <span>
              <strong>Use the auto-matched person.</strong>{' '}
              {preview.personMatch.name} (#{preview.personMatch.id})
            </span>
          </label>
        )}

        <label className="BulkUpload-ohActionOption">
          <input
            type="radio"
            name="personAction"
            value="search"
            checked={personAction === 'search'}
            onChange={() => setPersonAction('search')}
          />
          <span>
            <strong>Pick a different existing person.</strong>{' '}
            {pickedPersonId ? (
              <>
                Selected:{' '}
                <span className="BulkUpload-ohSearchBadge">
                  {pickedPersonName} (#{pickedPersonId})
                </span>
              </>
            ) : (
              <>Search the people table by name.</>
            )}
          </span>
        </label>

        {personAction === 'search' && (
          <div className="BulkUpload-ohSearch">
            <input
              type="text"
              className="AdminPortal-input"
              placeholder="Search people…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
            {searchBusy && (
              <p className="BulkUpload-ohSearchMeta">Searching…</p>
            )}
            {!searchBusy && searchQ.trim().length >= 2 && searchResults.length === 0 && (
              <p className="BulkUpload-ohSearchMeta">No results.</p>
            )}
            {searchResults.length > 0 && (
              <ul className="BulkUpload-ohSearchResults">
                {searchResults.map((r) => {
                  const isSelected = r.id === pickedPersonId;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        className={
                          'BulkUpload-ohLinkBtn' +
                          (isSelected ? ' BulkUpload-ohLinkBtn--selected' : '')
                        }
                        onClick={() => pickFromSearch(r)}
                      >
                        {isSelected ? '\u2713 ' : ''}
                        {r.name}{' '}
                        <span className="BulkUpload-ohMatchMeta">
                          #{r.id}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {pickedPersonId ? (
              <p className="BulkUpload-ohSearchMeta">
                Selected: <strong>{pickedPersonName}</strong> (#{pickedPersonId}){' '}
                <button
                  type="button"
                  className="BulkUpload-clearBtn BulkUpload-ohSearchClear"
                  onClick={clearSearchPick}
                >
                  Clear
                </button>
              </p>
            ) : (
              searchQ.trim().length < 2 && (
                <p className="BulkUpload-ohSearchMeta">
                  Type at least 2 characters to search.
                </p>
              )
            )}
          </div>
        )}

        <label className="BulkUpload-ohActionOption">
          <input
            type="radio"
            name="personAction"
            value="create"
            checked={personAction === 'create'}
            onChange={() => {
              setPersonAction('create');
              if (!newPersonName) setNewPersonName(editPerson || preview.person || '');
            }}
          />
          <span>
            <strong>Create a new person</strong> and link this interview to them.
          </span>
        </label>

        {personAction === 'create' && (
          <div className="BulkUpload-ohCreate">
            <label className="AdminPortal-field">
              <span className="AdminPortal-fieldLabel">Name</span>
              <input
                type="text"
                className="AdminPortal-input"
                value={newPersonName}
                onChange={(e) => setNewPersonName(e.target.value)}
              />
            </label>
            <label className="AdminPortal-field">
              <span className="AdminPortal-fieldLabel">Bio (optional)</span>
              <textarea
                className="AdminPortal-input BulkUpload-ohBio"
                value={newPersonBio}
                onChange={(e) => setNewPersonBio(e.target.value)}
                placeholder="Short biography — used for semantic search. Leave blank to auto-fill from the interview."
                rows={3}
              />
            </label>
          </div>
        )}

        <label className="BulkUpload-ohActionOption">
          <input
            type="radio"
            name="personAction"
            value="leave"
            checked={personAction === 'leave'}
            onChange={() => setPersonAction('leave')}
          />
          <span>
            <strong>Leave unlinked.</strong> Best for group interviews or when
            you&rsquo;re not sure who this is.
          </span>
        </label>
      </fieldset>
    </section>
  );
}

// ── Step 3: result ──

function DoneStep({ summary, onStartOver }) {
  return (
    <section className="AdminPortal-section">
      <h3 className="AdminPortal-sectionTitle">Upload complete</h3>
      <div className="AdminPortal-banner AdminPortal-banner--success">
        Inserted <strong>{summary.chunksInserted}</strong> new chunk
        {summary.chunksInserted === 1 ? '' : 's'}
        {summary.chunksSkippedDuplicate > 0 && (
          <>
            {' '}· skipped <strong>{summary.chunksSkippedDuplicate}</strong> duplicate
            {summary.chunksSkippedDuplicate === 1 ? '' : 's'}
          </>
        )}
        .
      </div>

      <ul className="BulkUpload-ohResultList">
        <li>
          <strong>File:</strong> {summary.fileName}
        </li>
        <li>
          <strong>Person:</strong> {summary.person}
          {summary.personId != null && <> (#{summary.personId})</>}
          {summary.personCreated && <> — <em>new row created</em></>}
          {summary.personId == null && <> — unlinked</>}
        </li>
        <li>
          <strong>Interviewer:</strong>{' '}
          {summary.interviewer || <em>none</em>}
        </li>
        <li>
          <strong>Date:</strong> {summary.date || <em>none</em>}
        </li>
        <li>
          <strong>Format:</strong>{' '}
          {FORMAT_LABEL[summary.detectedFormat] || summary.detectedFormat}
        </li>
        <li>
          <strong>Embeddings:</strong> {summary.embeddingSuccesses} generated
          {summary.embeddingFailures > 0 && (
            <>
              {' '}·{' '}
              <span className="BulkUpload-countDup">
                {summary.embeddingFailures} failed (rows kept; can be backfilled)
              </span>
            </>
          )}
        </li>
        {summary.fingerprintMatchedExisting && (
          <li>
            <strong>Fingerprint matched:</strong>{' '}
            <code>{summary.fingerprintMatchedExisting}</code>
          </li>
        )}
      </ul>

      <div className="AdminPortal-actions">
        <button
          type="button"
          className="ChatButton AdminPortal-submitBtn"
          onClick={onStartOver}
        >
          Upload another
        </button>
      </div>
    </section>
  );
}

// ── Helpers ──

function buildPersonDecision({
  personAction,
  preview,
  pickedPersonId,
  newPersonName,
  newPersonBio,
}) {
  if (personAction === 'leave') {
    return { action: 'leave' };
  }
  if (personAction === 'create') {
    if (!newPersonName?.trim()) return null;
    return {
      action: 'create',
      newPerson: {
        name: newPersonName.trim(),
        bio: newPersonBio?.trim() || undefined,
      },
    };
  }
  if (personAction === 'link') {
    if (!preview?.personMatch?.id) return null;
    return { action: 'link', personId: preview.personMatch.id };
  }
  if (personAction === 'search') {
    if (!pickedPersonId) return null;
    return { action: 'link', personId: pickedPersonId };
  }
  return null;
}
